import { useCallback } from 'react'
import { useWorld } from './use-world'

const GRID_SIZE = 20

class PriorityQueue {
  constructor() {
    this.items = []
  }

  enqueue(item, priority) {
    this.items.push({ item, priority })
    this.items.sort((a, b) => a.priority - b.priority)
  }

  dequeue() {
    return this.items.shift()?.item
  }

  isEmpty() {
    return this.items.length === 0
  }
}

const heuristic = (a, b) => {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y)
}

const posKey = (x, y) => `${x},${y}`

const getNeighbors = (pos, gridSize) => {
  const directions = [
    { x: gridSize, y: 0 },
    { x: -gridSize, y: 0 },
    { x: 0, y: gridSize },
    { x: 0, y: -gridSize },
    { x: gridSize, y: gridSize },
    { x: -gridSize, y: -gridSize },
    { x: gridSize, y: -gridSize },
    { x: -gridSize, y: gridSize },
  ]

  return directions.map((d) => ({
    x: pos.x + d.x,
    y: pos.y + d.y,
  }))
}

export const usePathfinding = () => {
  const { sprites, container } = useWorld()

  const isWalkable = useCallback(
    (x, y, width, height) => {
      if (x < 0 || y < 0) return false
      if (container && (x + width > container.width || y + height > container.height)) {
        return false
      }

      const testBox = { x, y, width, height }

      for (const key of Object.keys(sprites)) {
        if (key === 'player') continue
        const sprite = sprites[key]
        if (!sprite?.position) continue

        const spritePos = sprite.position
        if (
          spritePos.config?.solid !== false &&
          testBox.x < spritePos.x + spritePos.width &&
          testBox.x + testBox.width > spritePos.x &&
          testBox.y < spritePos.y + spritePos.height &&
          testBox.y + testBox.height > spritePos.y
        ) {
          return false
        }
      }

      return true
    },
    [sprites, container]
  )

  const findPath = useCallback(
    (start, goal, playerWidth, playerHeight) => {
      const gridSize = GRID_SIZE
      const checkWidth = Math.min(playerWidth, gridSize)
      const checkHeight = Math.min(playerHeight, gridSize)

      const startGrid = {
        x: Math.floor(start.x / gridSize) * gridSize,
        y: Math.floor(start.y / gridSize) * gridSize,
      }
      const goalGrid = {
        x: Math.floor(goal.x / gridSize) * gridSize,
        y: Math.floor(goal.y / gridSize) * gridSize,
      }

      if (startGrid.x === goalGrid.x && startGrid.y === goalGrid.y) {
        return [goalGrid]
      }

      if (!isWalkable(goalGrid.x, goalGrid.y, checkWidth, checkHeight)) {
        const nearbyOffsets = [
          { x: gridSize, y: 0 },
          { x: -gridSize, y: 0 },
          { x: 0, y: gridSize },
          { x: 0, y: -gridSize },
          { x: gridSize, y: gridSize },
          { x: -gridSize, y: -gridSize },
          { x: gridSize, y: -gridSize },
          { x: -gridSize, y: gridSize },
        ]
        let found = false
        for (const offset of nearbyOffsets) {
          const testX = goalGrid.x + offset.x
          const testY = goalGrid.y + offset.y
          if (isWalkable(testX, testY, checkWidth, checkHeight)) {
            goalGrid.x = testX
            goalGrid.y = testY
            found = true
            break
          }
        }
        if (!found) return []
      }

      const openSet = new PriorityQueue()
      const cameFrom = new Map()
      const gScore = new Map()
      const fScore = new Map()

      const startKey = posKey(startGrid.x, startGrid.y)
      gScore.set(startKey, 0)
      fScore.set(startKey, heuristic(startGrid, goalGrid))
      openSet.enqueue(startGrid, fScore.get(startKey))

      const visited = new Set()
      let iterations = 0
      const maxIterations = 1000

      while (!openSet.isEmpty() && iterations < maxIterations) {
        iterations++
        const current = openSet.dequeue()
        const currentKey = posKey(current.x, current.y)

        if (current.x === goalGrid.x && current.y === goalGrid.y) {
          const path = []
          let curr = current
          while (curr) {
            path.unshift({ x: curr.x, y: curr.y })
            curr = cameFrom.get(posKey(curr.x, curr.y))
          }
          return path
        }

        if (visited.has(currentKey)) continue
        visited.add(currentKey)

        for (const neighbor of getNeighbors(current, gridSize)) {
          const neighborKey = posKey(neighbor.x, neighbor.y)

          if (visited.has(neighborKey)) continue

          const isStart = neighbor.x === startGrid.x && neighbor.y === startGrid.y
          if (!isStart && !isWalkable(neighbor.x, neighbor.y, checkWidth, checkHeight)) continue

          const isDiagonal = neighbor.x !== current.x && neighbor.y !== current.y
          const moveCost = isDiagonal ? 1.414 : 1
          const tentativeG = (gScore.get(currentKey) || Infinity) + moveCost

          if (tentativeG < (gScore.get(neighborKey) || Infinity)) {
            cameFrom.set(neighborKey, current)
            gScore.set(neighborKey, tentativeG)
            const f = tentativeG + heuristic(neighbor, goalGrid)
            fScore.set(neighborKey, f)
            openSet.enqueue(neighbor, f)
          }
        }
      }

      return []
    },
    [isWalkable]
  )

  return { findPath, isWalkable }
}
